import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  verifyCohortAccess,
  fetchCurriculum,
  fetchChallenge,
  fetchLeaderboard,
  markVideoComplete,
  clearSessionExpired,
} from "../../features/cohort/cohortSlice";
import CohortSidebar from "./components/CohortSidebar";
import ChallengeWorkspace from "./components/ChallengeWorkspace";
import VideoPlayer from "./components/VideoPlayer";
import QuizWorkspace from "./components/QuizWorkspace";
import Confetti from "react-confetti";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700&display=swap');

  .font-display { font-family: 'Outfit', sans-serif; }
  .font-mono { font-family: 'JetBrains Mono', monospace; }
`;

function CodeCohortLearn() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { challengeSlug } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const previousCourseCompletionRef = useRef({});
  const isInitialLoadRef = useRef(true);

  const { user, fetchingUser } = useSelector((state) => state.auth);
  const {
    hasAccess,
    accessChecked,
    curriculum,
    currentChallenge,
    leaderboard,
    myProgress,
    isLoading,
    sessionExpired,
  } = useSelector((state) => state.cohort);

  // Verify access and load data on mount
  useEffect(() => {
    const initializeData = async () => {
      const accessResult = await dispatch(verifyCohortAccess());

      if (accessResult.payload?.hasAccess) {
        dispatch(fetchCurriculum());
      }
    };

    // Don't initialize data while still fetching user
    if (fetchingUser) {
      return;
    }

    if (user?.[0]?.email) {
      initializeData();
    }
  }, [dispatch, user, fetchingUser]);

  // Handle session expiry
  useEffect(() => {
    if (sessionExpired) {
      alert("Your session has expired. Please log in again.");
      dispatch(clearSessionExpired());
      navigate("/auth", { state: { returnTo: "/6-week-challenge/learn" } });
    }
  }, [sessionExpired, dispatch, navigate]);

  // Redirect if not logged in or no access
  useEffect(() => {
    // Don't redirect while still fetching user data
    if (fetchingUser) {
      return;
    }

    if (!user?.[0]?.email) {
      navigate("/auth", { state: { returnTo: "/6-week-challenge/learn" } });
    } else if (accessChecked && hasAccess === false) {
      // Only redirect if we've checked access and confirmed no access
      navigate("/6-week-challenge");
    }
  }, [user, hasAccess, accessChecked, isLoading, fetchingUser, navigate]);

  // Load challenge from slug or first available
  useEffect(() => {
    if (curriculum.length === 0) return;

    let challengeToLoad = null;

    if (challengeSlug) {
      // Find challenge by slug
      for (const week of curriculum) {
        const challenge = week.items.find((item) => item.slug === challengeSlug);
        if (challenge) {
          challengeToLoad = challenge;
          break;
        }
      }
    } else if (!currentChallenge) {
      // Only load first unlocked challenge if no challenge is currently loaded
      for (const week of curriculum) {
        if (!week.released) continue;
        const firstUnlocked = week.items.find((item) => item.unlocked);
        if (firstUnlocked) {
          challengeToLoad = firstUnlocked;
          break;
        }
      }
    }

    if (challengeToLoad && challengeToLoad.id !== currentChallenge?.challenge?.id) {
      dispatch(fetchChallenge(challengeToLoad.id));
    }
  }, [curriculum, challengeSlug, dispatch, currentChallenge]);

  const handleSelectChallenge = (challenge) => {
    if (challenge.slug) {
      navigate(`/6-week-challenge/learn/${challenge.slug}`);
    }
    dispatch(fetchChallenge(challenge.id));
  };

  const handleMarkVideoComplete = () => {
    if (currentChallenge?.challenge?.id) {
      dispatch(markVideoComplete(currentChallenge.challenge.id));
    }
  };

  const handleFetchLeaderboard = () => {
    dispatch(fetchLeaderboard());
  };

  // Update window size for confetti
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);
    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  // Check for course completion and trigger confetti
  useEffect(() => {
    if (curriculum.length === 0) return;

    curriculum.forEach((course) => {
      const allChallenges = course.items;
      const completedChallenges = allChallenges.filter(
        (item) => item.status === "Completed"
      );
      const isCourseCompleted =
        allChallenges.length > 0 &&
        completedChallenges.length === allChallenges.length;

      const courseId = course.id.toString();
      const wasCompleted = previousCourseCompletionRef.current[courseId];

      // On initial load, just record the current state without triggering confetti
      if (isInitialLoadRef.current) {
        previousCourseCompletionRef.current[courseId] = isCourseCompleted;
        return;
      }

      // If course just became completed (wasn't before, but is now)
      if (isCourseCompleted && !wasCompleted) {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000); // Show for 5 seconds
      }

      // Update the ref to track current state
      previousCourseCompletionRef.current[courseId] = isCourseCompleted;
    });

    // After first pass through curriculum, mark initial load as complete
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
    }
  }, [curriculum]);

  if (isLoading && curriculum.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <style>{styles}</style>
        <div className="text-zinc-400 font-display">Loading...</div>
      </div>
    );
  }

  const selectedChallengeFromCurriculum = curriculum
    .flatMap((week) => week.items)
    .find((item) => item.id === currentChallenge?.challenge?.id);

  const currentWeek = curriculum.find((week) =>
    week.items.some((item) => item.id === currentChallenge?.challenge?.id)
  );

  const isVideoChallenge = currentChallenge?.challenge?.type === "video";
  const isQuizChallenge = currentChallenge?.challenge?.type === "quiz";

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-zinc-100">
      <style>{styles}</style>

      {/* Confetti */}
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={500}
          gravity={0.3}
        />
      )}

      {/* Top Navigation Bar */}
      <div className="h-14 bg-zinc-900/90 border-b border-zinc-800 flex items-center justify-between px-4 sticky top-0 z-10">
        {/* Left: Sidebar toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 text-zinc-400 hover:text-zinc-200 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Center: Current Progress */}
        <div className="hidden md:flex items-center gap-2 text-sm">
          {currentWeek && (
            <>
              <span className="text-zinc-500">Week {currentWeek.week}</span>
              <span className="text-zinc-700">/</span>
            </>
          )}
          <span className="text-amber-400 font-medium">
            {currentChallenge?.challenge?.name || "Select a challenge"}
          </span>
        </div>

        {/* Right: User info */}
        <div className="text-sm text-zinc-500">
          {myProgress?.totalPoints > 0 && (
            <span className="text-emerald-400 font-mono">{myProgress.totalPoints} pts</span>
          )}
        </div>
      </div>

      <div className="flex h-[calc(100vh-3.5rem)]">
        {/* Sidebar */}
        <div
          className={`${
            sidebarOpen ? "w-80" : "w-0"
          } bg-zinc-900/50 border-r border-zinc-800 flex flex-col transition-all duration-200 overflow-hidden flex-shrink-0 h-full`}
        >
          <CohortSidebar
            curriculum={curriculum}
            leaderboard={leaderboard}
            myProgress={myProgress}
            selectedChallenge={selectedChallengeFromCurriculum}
            onSelectChallenge={handleSelectChallenge}
            onFetchLeaderboard={handleFetchLeaderboard}
          />
        </div>

        {/* Main Workspace */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!currentChallenge ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <svg
                  className="w-16 h-16 text-zinc-600 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="font-display text-zinc-500">Select a challenge from the sidebar</p>
              </div>
            </div>
          ) : isVideoChallenge ? (
            <VideoPlayer
              videoUrl={currentChallenge.challenge.videoUrl}
              challengeTitle={currentChallenge.challenge.name}
              onMarkComplete={handleMarkVideoComplete}
              isSubmitting={false}
            />
          ) : isQuizChallenge ? (
            <QuizWorkspace
              challenge={currentChallenge.challenge}
              quizData={currentChallenge.quizData}
              progress={currentChallenge.progress}
            />
          ) : (
            <ChallengeWorkspace
              challenge={currentChallenge.challenge}
              progress={currentChallenge.progress}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CodeCohortLearn;
